import {format} from 'date-fns';
import {useNavigate} from 'react-router-dom';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import {appPaths} from '@/config/appPaths';
import type {AdminBusinessUpdateListItem} from '@/types';
import {BusinessUpdateStatus} from '@/types';

import {CATEGORY_STYLES, STATUS_STYLES} from '../categoryStyles';

const formatDate = (value?: string) =>
  value ? format(new Date(value), 'dd MMM yyyy') : '—';

// Mirrors the server: only Draft or Scheduled updates can be deleted.
const DELETABLE_STATUSES = [
  BusinessUpdateStatus.Draft,
  BusinessUpdateStatus.Scheduled,
];

const ProductUpdatesTable = ({
  items,
  onDelete,
}: {
  items: AdminBusinessUpdateListItem[];
  onDelete?: (item: AdminBusinessUpdateListItem) => void;
}) => {
  const navigate = useNavigate();

  return (
    <Table className="overflow-hidden">
      <Thead>
        <Tr enableHover={false}>
          <Th>Title</Th>
          <Th>Category</Th>
          <Th>Status</Th>
          <Th>Author</Th>
          <Th>Published</Th>
          <Th>&nbsp;</Th>
        </Tr>
      </Thead>
      <TBody>
        {items.map((item) => {
          const category = CATEGORY_STYLES[item.category];
          const status = STATUS_STYLES[item.status];
          return (
            <Tr
              key={item.id}
              className="cursor-pointer"
              // Leading slash needed, see goToList in useProductUpdateCompose.
              onClick={() => navigate(`/${appPaths.productUpdates}/${item.id}`)}
            >
              <Td className="font-medium">{item.title}</Td>
              <Td>
                {category && (
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${category.className}`}
                  >
                    {category.label}
                  </span>
                )}
              </Td>
              <Td>
                {status && (
                  <span
                    className={`inline-flex items-center gap-1.5 text-sm font-medium ${status.className}`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${status.dotClassName}`}
                    />
                    {status.label}
                  </span>
                )}
              </Td>
              <Td>
                {item.authorName}
                {item.authorRoleLabel ? ` · ${item.authorRoleLabel}` : ''}
              </Td>
              <Td>{formatDate(item.publishedAt || item.publishAt)}</Td>
              <Td>
                {onDelete && DELETABLE_STATUSES.includes(item.status) ? (
                  <PermissionsProvider
                    permission={AfriexAdminPermission.BusinessUpdateManage}
                  >
                    <Button
                      variant="outline"
                      colorScheme="red"
                      onClick={(event) => {
                        event.stopPropagation();
                        onDelete(item);
                      }}
                    >
                      Delete
                    </Button>
                  </PermissionsProvider>
                ) : (
                  <>&nbsp;</>
                )}
              </Td>
            </Tr>
          );
        })}
      </TBody>
    </Table>
  );
};

export default ProductUpdatesTable;
